import { MetaDataLabel, SubheadingLabel } from '@/styles/fonts'
import { ApiClient } from '@grandstand-web/bally-web-shared/src/newPackages/ApiClient'
import { AnalyticServiceContext } from '@grandstand-web/bally-web-shared/src/services/analytics/AnalyticService'
import {
  ConfigServiceContext,
  useContextUnconditionally,
} from '@grandstand-web/bally-web-shared/src/services/config/ConfigService'
import { useTealium } from '@grandstand-web/bally-web-shared/src/services/tealium/TealiumService'
import { UserServiceContext } from '@grandstand-web/bally-web-shared/src/services/user/UserService'
import { delay } from '@grandstand-web/bally-web-shared/src/utils/queryUtils'
import {
  ActiveSubscriptionsResponse,
  CancelSubscriptionResponse,
  HTTPErrorResponse,
  PauseSubscriptionResponse,
  ReactivateSubscriptionResponse,
  ResumeSubscriptionResponse,
} from '@grandstand/presentation-models'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useContext, useRef, useState } from 'react'
import styled from 'styled-components'
import Modal from '../Modal'

type ModalVariantName =
  | 'PAUSE_PROMPT'
  | 'PAUSE_CONFIRM'
  | 'PAUSE_SUCCESS'
  | 'RESUME_PROMPT'
  | 'RESUME_SUCCESS'
  | 'CANCEL_SEASONAL'
  | 'CANCEL_PROMPT_SEASONAL'
  | 'CANCEL_PROMPT' // disables auto billing
  | 'CANCEL_SUCCESS'
  | 'RENEW_PROMPT' // (Restore a canceled sub, aka resume auto billing)
  | 'RENEW_SUCCESS'
  | 'GENERIC_ERROR'

const PauseDurationSelect = styled.select`
  width: 464px;
  height: 56px;
  padding: 16px;
  background: var(--surface);
  color: var(--on-background-alt);
  border-radius: 4px;
  border: 1px solid var(--web-light-divider-canvas, #dadada);
  &:focus {
    border: 1px solid var(--web-light-divider-canvas, #dadada);
  }
`
const PauseDurationOption = styled.option`
  background: var(--surface);
`
const PauseDurationSelectText = styled(MetaDataLabel)`
  color: var(--web-light-on-background-alt, #6f7274);
`
const ModalMessageText = styled(SubheadingLabel)<{ bold?: boolean }>`
  color: var(--on-background, #000);
  text-align: center;
  margin-bottom: 16px;
  font-weight: ${(props) => (props.bold ? 'bold' : 'normal')};
`
const ISO8601ToDate = (iso8601: string | null) => {
  if (!iso8601) {
    return ''
  }
  return new Date(iso8601).toLocaleDateString()
}

const ModalVariant = ({
  subscription,
  billingResumeDate,
  modalVariant,
  orderId,
  pauseEndDate,
  serviceId,
  onDismiss,
  setBillingResumeDate,
  setModalVariant,
  setPauseEndDate,
}: {
  subscription: ActiveSubscriptionsResponse[number]
  billingResumeDate: string | null
  modalVariant: ModalVariantName
  orderId: string
  pauseEndDate: string | null
  serviceId: string
  onDismiss: () => void
  setBillingResumeDate: Dispatch<SetStateAction<string | null>>
  setModalVariant: Dispatch<SetStateAction<ModalVariantName>>
  setPauseEndDate: Dispatch<SetStateAction<string | null>>
}) => {
  const router = useRouter()
  const { currentConfig } = useContextUnconditionally(ConfigServiceContext)
  const userService = useContext(UserServiceContext)
  const analyticService = useContext(AnalyticServiceContext)
  const tealium = useTealium()
  const pauseDurationRef = useRef<HTMLSelectElement>(null)
  const [errMsg, setErrMsg] = useState<string | null>(null)
  const [managementActionConfirmed, setManagementActionConfirmed] = useState(false)

  const getPauseDurationLabel = (duration: number) => {
    return `${duration} Month${duration > 1 ? 's' : ''}`
  }

  if (!errMsg) {
    switch (modalVariant) {
      case 'PAUSE_PROMPT':
        return (
          <Modal
            title="Pause your subscription?"
            msg="You can pause your subscription for up to 6 months and resume at anytime."
            buttons={[
              {
                label: 'PAUSE SUBSCRIPTION',
                onClick: () => {
                  setModalVariant('PAUSE_CONFIRM')
                },
              },
            ]}
            footerLink={{
              label: 'Cancel subscription',
              onClick: () => {
                setModalVariant('CANCEL_PROMPT')
              },
            }}
            onDismiss={onDismiss}
          />
        )
      case 'PAUSE_CONFIRM':
        return (
          <Modal
            title="How long do you want to pause?"
            msg="You can pause your subscription for up to 6 months. Billing will resume at the end of that duration."
            buttons={[
              {
                label: 'PAUSE SUBSCRIPTION',
                onClick: () => {
                  if (pauseDurationRef.current && !managementActionConfirmed) {
                    const duration = parseInt(pauseDurationRef.current.value)
                    const durationLabel = getPauseDurationLabel(duration)
                    setManagementActionConfirmed(true)
                    ApiClient.convenientApiFetch({
                      url: currentConfig.API.services.subscription_services.pause,
                      method: 'PATCH',
                      body: {
                        order_id: orderId,
                        service_id: serviceId,
                        pause_duration: duration,
                      },
                    })
                      .then((data) => data.json())
                      .then((resp: PauseSubscriptionResponse | HTTPErrorResponse) => {
                        if ('status' in resp && 'code' in resp) {
                          if (resp.message.startsWith('Upstream Evergent API failed to respond within')) {
                            // Since there is no way for client to recover from this early timeout from middleware,
                            // we give them some extra time to hopefully complete the transaction and assume it succeeded.
                            // The page will reload after user confirms success if the status does not show the updated state
                            // then we expect the issue to be reported to customer care.
                          } else {
                            setErrMsg(resp.message)
                          }
                        } else {
                          setPauseEndDate(resp.paused_subscription.pause_expiration)
                          analyticService.pauseSubscription()
                          setModalVariant('PAUSE_SUCCESS')
                        }
                      })
                      .catch((e) => {
                        console.error(e)
                        setModalVariant('GENERIC_ERROR')
                      })
                  }
                },
              },
            ]}
            onDismiss={onDismiss}
            showLoading={managementActionConfirmed}
          >
            <PauseDurationSelect ref={pauseDurationRef}>
              {[1, 2, 3, 4, 5, 6].map((entry) => (
                <PauseDurationOption key={entry} value={entry}>
                  {getPauseDurationLabel(entry)}
                </PauseDurationOption>
              ))}
            </PauseDurationSelect>
          </Modal>
        )
      case 'PAUSE_SUCCESS':
        const pauseExpiration = ISO8601ToDate(pauseEndDate)
        return (
          <Modal
            title="Subscription paused."
            msg={`You have paused your subscription for ${pauseDurationRef.current?.value ?? ''} month${
              parseInt(pauseDurationRef.current?.value ?? '0') > 1 ? 's' : ''
            }. Billing will resume${pauseExpiration === '' ? pauseExpiration : `on ${pauseExpiration}`}.`}
            buttons={[
              {
                label: 'CLOSE',
                onClick: () => {
                  router.reload()
                },
              },
            ]}
            onDismiss={onDismiss}
          />
        )
      case 'RESUME_PROMPT':
        return (
          <Modal
            title="Resume your subscription?"
            msg="Resume your subscription and start streaming now, or cancel."
            buttons={[
              {
                label: 'RESUME SUBSCRIPTION',
                onClick: () => {
                  if (!managementActionConfirmed) {
                    setManagementActionConfirmed(true)
                    ApiClient.convenientApiFetch({
                      url: currentConfig.API.services.subscription_services.resume,
                      method: 'PATCH',
                      body: {
                        order_id: orderId,
                        service_id: serviceId,
                        service_type: 'PACKAGE',
                      },
                    })
                      .then((data) => data.json())
                      .then((resp: ResumeSubscriptionResponse | HTTPErrorResponse) => {
                        if ('status' in resp && 'code' in resp) {
                          if (resp.message.startsWith('Upstream Evergent API failed to respond within')) {
                            // Since there is no way for client to recover from this early timeout from middleware,
                            // we give them some extra time to hopefully complete the transaction and assume it succeeded.
                            // The page will reload after user confirms success if the status does not show the updated state
                            // then we expect the issue to be reported to customer care.
                            delay(5000).then(() => {
                              setModalVariant('RESUME_SUCCESS')
                              tealium.resumeSubscription()
                            })
                          } else {
                            setErrMsg(resp.message)
                          }
                        } else {
                          setBillingResumeDate(resp.resumed_subscription.next_bill_date)
                          setModalVariant('RESUME_SUCCESS')
                          analyticService.resumeSubscription()
                          tealium.resumeSubscription()
                        }
                      })
                      .catch((e) => {
                        console.error(e)
                        setModalVariant('GENERIC_ERROR')
                      })
                  }
                },
              },
            ]}
            onDismiss={onDismiss}
            showLoading={managementActionConfirmed}
          />
        )
      case 'RESUME_SUCCESS':
        return (
          <Modal
            title="Subscription resumed."
            msg={
              <>
                <ModalMessageText fontSize="1">
                  {'Glad to have you back, champ. You have resumed your subscription. Billing will resume on '}
                </ModalMessageText>
                <ModalMessageText fontSize="1" bold={true}>
                  {ISO8601ToDate(billingResumeDate)}
                </ModalMessageText>
              </>
            }
            buttons={[
              {
                label: 'CLOSE',
                onClick: () => {
                  router.reload()
                },
              },
            ]}
            onDismiss={() => {
              onDismiss()
              router.reload()
            }}
          />
        )
      case 'CANCEL_SEASONAL':
        return (
          <Modal
            title="Cancel your subscription?"
            msg="Your subscription will remain active until the next billing date. You will lose access to content once your subscription expires."
            buttons={[
              {
                label: 'CANCEL MY SUBSCRIPTION',
                onClick: () => {
                  setModalVariant('CANCEL_PROMPT_SEASONAL')
                },
              },
            ]}
            onDismiss={onDismiss}
          />
        )
      case 'CANCEL_PROMPT_SEASONAL':
        return (
          <Modal
            title="You will lose access to premium content once your subscription expires."
            msg="Your subscription will remain active until the next billing date."
            buttons={[
              {
                label: 'CANCEL SUBSCRIPTION',
                onClick: () => {
                  if (!managementActionConfirmed) {
                    setManagementActionConfirmed(true)
                    ApiClient.convenientApiFetch({
                      url: currentConfig.API.services.subscription_services.cancel,
                      method: 'PATCH',
                      body: {
                        order_id: orderId,
                        service_id: serviceId,
                        service_type: 'PACKAGE',
                      },
                    })
                      .then((data) => data.json())
                      .then((resp: CancelSubscriptionResponse | HTTPErrorResponse) => {
                        if ('status' in resp && 'code' in resp) {
                          if (resp.message.startsWith('Upstream Evergent API failed to respond within')) {
                            // Since there is no way for client to recover from this early timeout from middleware,
                            // we give them some extra time to hopefully complete the transaction and assume it succeeded.
                            // The page will reload after user confirms success if the status does not show the updated state
                            // then we expect the issue to be reported to customer care.
                            delay(5000).then(() => {
                              setModalVariant('CANCEL_SUCCESS')
                              analyticService.cancelledSubscription()
                            })
                          } else {
                            // todo: ignore evergent error
                            setErrMsg(resp.message)
                          }
                        } else {
                          setModalVariant('CANCEL_SUCCESS')
                          analyticService.cancelledSubscription()
                        }
                      })
                      .catch((e) => {
                        console.error(e)
                        setModalVariant('GENERIC_ERROR')
                      })
                  }
                },
              },
            ]}
            onDismiss={onDismiss}
            showLoading={managementActionConfirmed}
          />
        )
      case 'CANCEL_PROMPT':
        return (
          <Modal
            title="You will lose access to premium content once your subscription expires."
            msg="Your subscription will remain active until the next billing date."
            buttons={[
              {
                label: 'CANCEL SUBSCRIPTION',
                onClick: () => {
                  if (!managementActionConfirmed) {
                    setManagementActionConfirmed(true)
                    ApiClient.convenientApiFetch({
                      url: currentConfig.API.services.subscription_services.cancel,
                      method: 'PATCH',
                      body: {
                        order_id: orderId,
                        service_id: serviceId,
                        service_type: 'PACKAGE',
                      },
                    })
                      .then((data) => data.json())
                      .then((resp: CancelSubscriptionResponse | HTTPErrorResponse) => {
                        if ('status' in resp && 'code' in resp) {
                          if (resp.message.startsWith('Upstream Evergent API failed to respond within')) {
                            // Since there is no way for client to recover from this early timeout from middleware,
                            // we give them some extra time to hopefully complete the transaction and assume it succeeded.
                            // The page will reload after user confirms success if the status does not show the updated state
                            // then we expect the issue to be reported to customer care.
                            delay(5000).then(() => {
                              setModalVariant('CANCEL_SUCCESS')
                              analyticService.cancelledSubscription()
                            })
                          } else {
                            // todo: ignore evergent message
                            setErrMsg(resp.message)
                          }
                        } else {
                          setModalVariant('CANCEL_SUCCESS')
                          analyticService.cancelledSubscription()
                        }
                      })
                      .catch((e) => {
                        console.error(e)
                        setModalVariant('GENERIC_ERROR')
                      })
                  }
                },
              },
            ]}
            footerLink={{
              label: 'Pause my subscription instead',
              onClick: () => {
                setModalVariant('PAUSE_CONFIRM')
              },
            }}
            onDismiss={onDismiss}
            showLoading={managementActionConfirmed}
          />
        )
      case 'CANCEL_SUCCESS':
        let buttons = [
          {
            label: 'CLOSE',
            onClick: () => {
              router.reload()
            },
          },
        ]
        buttons = [
          {
            label: 'LEAVE FEEDBACK',
            onClick: () => {
              if (currentConfig.API.services.subscription_services.cancel_survey) {
                window.open(currentConfig.API.services.subscription_services.cancel_survey, '_blank')
              }
              setModalVariant('CANCEL_SUCCESS')
            },
          },
          buttons[0],
        ]
        return (
          <Modal
            title="Subscription cancelled."
            msg="Your subscription has been cancelled. You will no longer have access to content."
            buttons={buttons}
            onDismiss={onDismiss}
          />
        )
      case 'RENEW_PROMPT':
        return (
          <Modal
            title="Renew subscription?"
            msg="Looks like your subscription is set to expire. Renew now to continue watching without interruption."
            buttons={[
              {
                label: 'RENEW SUBSCRIPTION',
                onClick: () => {
                  if (!managementActionConfirmed) {
                    setManagementActionConfirmed(true)
                    ApiClient.convenientApiFetch({
                      url: currentConfig.API.services.subscription_services.reactivate,
                      method: 'PATCH',
                      body: {
                        order_id: orderId,
                        service_id: serviceId,
                        service_type: 'PACKAGE',
                      },
                    })
                      .then((data) => data.json())
                      .then((resp: ReactivateSubscriptionResponse | HTTPErrorResponse) => {
                        if ('status' in resp && 'code' in resp) {
                          if (resp.message.startsWith('Upstream Evergent API failed to respond within')) {
                            // Since there is no way for client to recover from this early timeout from middleware,
                            // we give them some extra time to hopefully complete the transaction and assume it succeeded.
                            // The page will reload after user confirms success if the status does not show the updated state
                            // then we expect the issue to be reported to customer care.
                            delay(5000).then(() => {
                              tealium.restoreSubscription(resp, subscription)
                              setModalVariant('RENEW_SUCCESS')
                            })
                          } else {
                            setErrMsg(resp.message)
                          }
                        } else {
                          setBillingResumeDate(resp.next_bill_date)
                          tealium.restoreSubscription(resp, subscription)
                          setModalVariant('RENEW_SUCCESS')
                        }
                      })
                      .catch((e) => {
                        console.error(e)
                        setModalVariant('GENERIC_ERROR')
                      })
                  }
                },
              },
            ]}
            onDismiss={onDismiss}
            showLoading={managementActionConfirmed}
          />
        )
      case 'RENEW_SUCCESS':
        return (
          <Modal
            title="Subscription renewed."
            msg={
              <>
                <ModalMessageText fontSize="1">
                  {'Keep racking up the wins. Billing will resume on'}
                  &nbsp;<span style={{ fontWeight: 'bold' }}>{ISO8601ToDate(billingResumeDate)}</span>.
                </ModalMessageText>
              </>
            }
            buttons={[
              {
                label: 'CLOSE',
                onClick: () => {
                  router.reload()
                },
              },
            ]}
            onDismiss={async () => {
              onDismiss()
              router.reload()
            }}
          />
        )
      case 'GENERIC_ERROR':
        return (
          <Modal
            title="Oops it looks like something went wrong!"
            msg={'Please contact the support team.'}
            buttons={[
              {
                label: 'CLOSE',
                onClick: () => {
                  onDismiss()
                },
              },
            ]}
            canDismiss={false}
          />
        )
      default:
        return null
    }
  } else {
    return (
      <Modal
        title="Oops it looks like something went wrong!"
        msg={errMsg}
        buttons={[
          {
            label: 'CLOSE',
            onClick: () => {
              onDismiss()
            },
          },
        ]}
        canDismiss={false}
      />
    )
  }
}

const SubscriptionManager = ({
  subscription,
  onDismiss,
}: {
  subscription: ActiveSubscriptionsResponse[number]
  onDismiss: () => void
}) => {
  const getInitialModalVariant = (status: string, productType: string): ModalVariantName => {
    if (productType === 'package-seasonal' && status === 'active') {
      return 'CANCEL_SEASONAL'
    } else {
      switch (status) {
        case 'paused':
          return 'RESUME_PROMPT'
        case 'canceled':
          return 'RENEW_PROMPT'
        default:
          return 'PAUSE_PROMPT'
      }
    }
  }
  const [modalVariant, setModalVariant] = useState<ModalVariantName>(
    getInitialModalVariant(subscription.subscription_status, subscription.dtc_product_type)
  )
  const [pauseEndDate, setPauseEndDate] = useState<string | null>(null)
  const [billingResumeDate, setBillingResumeDate] = useState<string | null>(null)
  const { order_id, service_id } = subscription

  return (
    <ModalVariant
      subscription={subscription}
      modalVariant={modalVariant}
      setModalVariant={setModalVariant}
      onDismiss={onDismiss}
      orderId={order_id}
      serviceId={service_id}
      pauseEndDate={pauseEndDate}
      setPauseEndDate={setPauseEndDate}
      billingResumeDate={billingResumeDate}
      setBillingResumeDate={setBillingResumeDate}
    />
  )
}

export default SubscriptionManager
